import React, { useEffect, useRef, useState } from "react"

import moment from "moment"

import "./timePicker.css"

const ITEM_HEIGHT = 40
const VISIBLE_ITEMS = 5

type DateTimeState = {
  startDate: string
  startTime: string
  endDate: string
  endTime: string
  activeField: "startDate" | "startTime" | "endDate" | "endTime"
}

const TimePicker = ({
  selectedTime,
  onChange = () => {},
  dateTimeState,
  allowSelectingPastDates = false,
}: {
  selectedTime: string
  onChange: (time: string) => void
  dateTimeState: DateTimeState
  allowSelectingPastDates?: boolean
}) => {
  const parsed = moment(selectedTime, ["hh:mm A"])
  const initialHour = parsed.isValid() ? parsed.hour() : 12
  const initialMinute = parsed.isValid() ? parsed.minute() : 0
  const initialAmpm = parsed.isValid() ? parsed.format("A") : "AM"

  // Convert to 12-hour format for display
  const displayHour = initialHour % 12 || 12

  const minMoment = moment(
    dateTimeState.startDate + " " + dateTimeState.startTime,
    ["YYYY-MM-DD hh:mm A"],
  )
  const maxMoment = moment(
    dateTimeState.endDate + " " + dateTimeState.endTime,
    ["YYYY-MM-DD hh:mm A"],
  )

  const isEndDateLater = allowSelectingPastDates
    ? true
    : moment(dateTimeState.endDate).isAfter(
        moment(dateTimeState.startDate),
        "day",
      )

  const [hour, setHour] = useState(displayHour)
  const [minute, setMinute] = useState(initialMinute)
  const [ampm, setAmpm] = useState(initialAmpm)

  const hourRef = useRef<HTMLDivElement>(null)
  const minuteRef = useRef<HTMLDivElement>(null)
  const ampmRef = useRef<HTMLDivElement>(null)

  const hours = Array.from({ length: 12 }, (_, i) => i + 1)
  const minutes = Array.from({ length: 60 }, (_, i) => i)
  const ampmArr = ["AM", "PM"]

  const scrollTo = (ref: React.RefObject<HTMLDivElement>, index: number) => {
    if (ref.current) {
      ref.current.scrollTo({
        top: index * ITEM_HEIGHT,
        behavior: "smooth",
      })
    }
  }

  // Scroll to initial selected values
  useEffect(() => {
    const hIdx = hours.indexOf(hour)
    const mIdx = minutes.indexOf(minute)
    const apIdx = ampmArr.indexOf(ampm)
    console.log(
      "setting the scroll position",
      hour,
      minute,
      ampm,
      hours,
      minutes,
      ampmArr,
      typeof hIdx,
      typeof mIdx,
      typeof apIdx,
    )

    scrollTo(hourRef, hIdx)
    scrollTo(minuteRef, mIdx)
    scrollTo(ampmRef, apIdx)
  }, [])

  // Update parent when value changes
  useEffect(() => {
    const h = hour
    const mm = (minute ?? 0).toString().padStart(2, "0")
    const selectedTime = `${h}:${mm} ${ampm}`

    const newSelectedTime = moment(selectedTime, ["hh:mm A"])

    const maxSelectedTime = moment(dateTimeState.endTime, ["hh:mm A"])
    // dynamic minimum: if we're selecting startTime *and* startDate is today,
    // block everything before the next full hour; otherwise use the form's startTime
    const startDateMoment = moment(dateTimeState.startDate, "MMMM DD YYYY")
    let minSelectedTime = moment(dateTimeState.startTime, ["hh:mm A"])
    if (
      dateTimeState.activeField === "startTime" &&
      startDateMoment.isSame(moment(), "day")
    ) {
      // next full hour from now
      minSelectedTime = moment().add(1, "hour").startOf("hour")
    }

    if (dateTimeState.activeField === "startTime") {
      // always block past slots if startDate is today
      const startDateMoment = moment(dateTimeState.startDate, "MMMM DD YYYY")
      const isStartToday = startDateMoment.isSame(moment(), "day")

      // lower bound: next full hour if today, otherwise none
      const lowerBound = isStartToday
        ? moment().add(1, "hour").startOf("hour")
        : null

      const beforeLower = lowerBound && newSelectedTime.isBefore(lowerBound)

      if (beforeLower) {
        // snap up to lowerBound
        const snap = lowerBound!
        const hIdx = hours.indexOf(snap.hour() % 12 || 12)
        const mIdx = minutes.indexOf(snap.minute())
        const apIdx = ampmArr.indexOf(snap.format("A"))
        scrollTo(hourRef, hIdx)
        scrollTo(minuteRef, mIdx)
        scrollTo(ampmRef, apIdx)
      } else {
        // Allow any time selection - let DateTimePicker handle validation and auto-adjustment
        onChange(selectedTime)
      }
    } else {
      if (isEndDateLater) {
        onChange(selectedTime)
      } else {
        if (newSelectedTime.isSameOrBefore(minSelectedTime)) {
          const hIdx = hours.indexOf(maxSelectedTime.hour() % 12 || 12)
          const mIdx = minutes.indexOf(maxSelectedTime.minute())
          const apIdx = ampmArr.indexOf(maxSelectedTime.format("A"))
          scrollTo(hourRef, hIdx)
          scrollTo(minuteRef, mIdx)
          scrollTo(ampmRef, apIdx)
        } else {
          onChange(selectedTime)
        }
      }
    }
  }, [hour, minute, ampm, isEndDateLater, dateTimeState.activeField])

  const isClicked = useRef(false) // prevent double scroll event

  const handleScroll = (
    ref: React.RefObject<HTMLDivElement>,
    array: any[],
    setter: (val: any) => void,
    type: "hour" | "minute" | "ampm",
  ) => {
    if (!ref.current) return
    const idx = Math.round(ref.current.scrollTop / ITEM_HEIGHT)
    const value = array[idx]
    if (value !== undefined || value !== null) setter(value)
  }

  const renderWheel = (
    ref: React.RefObject<HTMLDivElement>,
    array: any[],
    selectedValue: any,
    setter: (val: any) => void,
    type: "hour" | "minute" | "ampm",
    formatter = (v: any) => v,
  ) => (
    <div
      className="wheel"
      ref={ref}
      onScroll={() => handleScroll(ref, array, setter, type)}
    >
      <div
        className="spacer"
        style={{ height: `${((VISIBLE_ITEMS - 1) / 2) * ITEM_HEIGHT}px` }}
      />
      {array.map((val, _idx) => (
        <div
          key={val}
          className={`wheel-item ${val === selectedValue ? "selected" : ""}`}
          onClick={async () => {
            ref.current?.scrollTo({
              top: _idx * ITEM_HEIGHT,
              behavior: "smooth",
            })
          }}
        >
          {formatter(val)}
        </div>
      ))}
      <div
        className="spacer"
        style={{ height: `${((VISIBLE_ITEMS - 1) / 2) * ITEM_HEIGHT}px` }}
      />
      <div className="selection-indicator" />
    </div>
  )

  return (
    <div className="flex flex-col items-center justify-center">
      <div className="time-picker">
        {renderWheel(hourRef, hours, hour, setHour, "hour")}
        {renderWheel(minuteRef, minutes, minute, setMinute, "minute", (m) =>
          m.toString().padStart(2, "0"),
        )}
        {renderWheel(ampmRef, ampmArr, ampm, setAmpm, "ampm")}
      </div>
    </div>
  )
}

export default TimePicker
